import { generate, templateLocationExists } from '../../../core/js/utils/componentUtils';
import { mDom, mDomMaintainInputVals } from '../../../core/js/utils/renderUtils';

import {
    changeStylistPreferredLocation,
    changeStylePreference,
    changeAppointmentType,
    changeHomeType,
    changeRoomPreference,
    setMonthForPreferredFirstChoice,
    setDayForPreferredFirstChoice,
    setTimeForPreferredFirstChoice,
    setMonthForPreferredSecondChoice,
    setDayForPreferredSecondChoice,
    setTimeForPreferredSecondChoice,
    setStoresForPersonalShopper,
    setStylistEmailAddress,
    setStylistLocationEmailAddress,
    setHomeStylistEmailAddress
} from '../actions/helpActions';

import {
    updateShopperAppointmentType,
    getStoresForPersonalShopper
} from '../sagas/sagas';

import {
    getRegionForPersonalShopperStore,
    getCountryForCountryCode
} from '../utils/utils';

import {
    MISL_BRANDS
} from '../../../core/js/enums/mislEnums';

export function getDefaultStylistEmailAddress(storesForPersonalShopper) {
    const keys = _.keys(storesForPersonalShopper);
    const defaultRegion = keys[0] || '';

    return _.get(storesForPersonalShopper, `${defaultRegion}.0.anthropologie.stylistEmail`, '');
}

export function getDefaultStoreEmailAddress(storesForPersonalShopper) {
    const keys = _.keys(storesForPersonalShopper);
    const defaultRegion = keys[0] || '';

    return _.get(storesForPersonalShopper, `${defaultRegion}.0.email`, '');
}

export function getStoresForRegion(state, storesForPersonalShopper, brandId) {
    return _(storesForPersonalShopper)
        .filter((personalShopperStore) => personalShopperStore.brandId === brandId)
        .map((personalShopperStore) => {
            const country = getCountryForCountryCode(state, personalShopperStore.country);
            const regionForStore = country ? getRegionForPersonalShopperStore(personalShopperStore, country) : '';

            personalShopperStore.regionDisplayName = regionForStore ?
                                                        regionForStore.description :
                                                        personalShopperStore.country;
            return personalShopperStore;
        })
        .orderBy(['regionDisplayName', 'city'], ['asc', 'asc'])
        .groupBy('regionDisplayName')
        .value();
}

export function afterConstruction(store) {
    store.runSaga(getStoresForPersonalShopper).done.then((response) => {
        const state = store.getState();
        const storesForUSRegion = getStoresForRegion(state, response.results, MISL_BRANDS['an-us']);
        const storesForUKRegion = getStoresForRegion(state, response.results, MISL_BRANDS['an-uk']);

        const storesForPersonalShopperList = $.extend(storesForUSRegion, storesForUKRegion);

        const defaultPreferredLocation = 'Instore';
        const defaultAppointmentType = 'personalShopper';

        store.dispatch(changeStylistPreferredLocation(defaultPreferredLocation));
        store.dispatch(changeAppointmentType(defaultAppointmentType));
        store.dispatch(setStoresForPersonalShopper(storesForPersonalShopperList));
        store.dispatch(setStylistEmailAddress(getDefaultStylistEmailAddress(storesForPersonalShopperList)));
        store.dispatch(setStylistLocationEmailAddress(getDefaultStoreEmailAddress(storesForPersonalShopperList)));
    });
}

/**
 * Set the default preferred location for appointment on page load
 * @param {String} preferredLocationOption - string for selecting which option to show by default
 * @returns {undefined}
 */
function selectDefaultPreferredLocation(preferredLocationOption) {
    $(preferredLocationOption).prop('checked', true);
}

function selectDefaultAppointmentType(appointmentTypeOption) {
    $(appointmentTypeOption).prop('checked', true);
}

/* eslint-disable complexity */
/**
 * Returns the appropriate choice for a preferred stylist appointment
 * @param  {object} store          The redux store
 * @param  {string} selectionType  Selected option name
 * @param  {string} selectionValue Selected option value
 * @returns {object}               Preferred choice
 */
function getChoice(store, selectionType, selectionValue) {
    let choice;

    switch (selectionType) {
        case 'firstChoiceMonth':
            choice = store.dispatch(setMonthForPreferredFirstChoice(selectionValue));
            break;
        case 'firstChoiceDay':
            choice = store.dispatch(setDayForPreferredFirstChoice(selectionValue));
            break;
        case 'firstChoiceTime':
            choice = store.dispatch(setTimeForPreferredFirstChoice(selectionValue));
            break;
        case 'secondChoiceMonth':
            choice = store.dispatch(setMonthForPreferredSecondChoice(selectionValue));
            break;
        case 'secondChoiceDay':
            choice = store.dispatch(setDayForPreferredSecondChoice(selectionValue));
            break;
        case 'secondChoiceTime':
            choice = store.dispatch(setTimeForPreferredSecondChoice(selectionValue));
            break;
        default:
    }

    return choice;
}

/* eslint-enable complexity */

/**
 * Attach component events to DOM
 * @param {object} store The redux store
 * @returns {undefined}
 */
export function attachEvents(store) {
    const selectedStylePreferences = [];
    const selectedRoomPreferences = [];
    // Select in store as preferred location for appointment by default
    // Moved out of template to only have this happen on first render of page
    selectDefaultAppointmentType('#personalShopper');
    selectDefaultPreferredLocation('#in-store');

    $(document).on(this.scopedEvent('change'), this.scopedSelector('.js-preferred-location-radio'), (e) => {
        const preferredLocationData = $(e.target).attr('data-preferred-location');

        store.dispatch(changeStylistPreferredLocation(preferredLocationData));
    });

    $(document).on(this.scopedEvent('change'), this.scopedSelector('.js-appointment-type-radio'), (e) => {
        const appointmentTypeData = $(e.target).attr('data-appointment-type');

        if(appointmentTypeData === 'personalShopper') {
            // NOTE Have to select in-store radio button
            // so the disabled ones do not get selected.
            selectDefaultPreferredLocation('#in-store');
            store.dispatch(changeStylistPreferredLocation('Instore'));
        }

        store.runSaga(updateShopperAppointmentType, appointmentTypeData);
    });

    $(document).on(this.scopedEvent('change'), this.scopedSelector('.js-home-type-select'), (e) => {
        const homeType = $(e.target).val();

        store.dispatch(changeHomeType(homeType));
    });

    $(document).on(this.scopedEvent('change'), this.scopedSelector('.js-room-preference-option'), (e) => {
        const roomPreferenceData = $(e.target).attr('data-room-preference');

        if (_.includes(selectedRoomPreferences, roomPreferenceData)) {
            _.pull(selectedRoomPreferences, roomPreferenceData);
        } else {
            selectedRoomPreferences.push(roomPreferenceData);
        }

        store.dispatch(changeRoomPreference(getPreferenceList(selectedRoomPreferences)));
    });

    $(document).on(this.scopedEvent('change'), this.scopedSelector('.js-style-preference-option'), (e) => {
        const stylePreferenceData = $(e.target).attr('data-style-preference');

        if (_.includes(selectedStylePreferences, stylePreferenceData)) {
            _.pull(selectedStylePreferences, stylePreferenceData);
        } else {
            selectedStylePreferences.push(stylePreferenceData);
        }
        store.dispatch(changeStylePreference(getPreferenceList(selectedStylePreferences)));
    });

    $(document).on(this.scopedEvent('change'), this.scopedSelector('.js-select-shopper-store'), (e) => {
        const stylistEmailAddress = $(e.target).val();
        const selectedStoreRegion = $(e.target)
            .find(':selected')
            .attr('data-region-code');
        const selectedStoreIndex = $(e.target)
            .find(':selected')
            .attr('data-loop-index');
        const storeEmailAddress = _.get(
            store.getState().core.genericEmailService.storesForPersonalShopper,
            `[${selectedStoreRegion}][${selectedStoreIndex}].email`,
            ''
        );
        const homeStylistEmail = _.get(
            store.getState().core.genericEmailService.storesForPersonalShopper,
            `[${selectedStoreRegion}][${selectedStoreIndex}].anthropologie.homeStylistEmail`,
            ''
        );

        store.dispatch(setStylistEmailAddress(stylistEmailAddress));
        store.dispatch(setStylistLocationEmailAddress(storeEmailAddress));
        store.dispatch(setHomeStylistEmailAddress(homeStylistEmail));
    });

    $(document).on(this.scopedEvent('change'), this.scopedSelector('.js-day-time-select'), (e) => {
        const selectionType = $(e.target).attr('name');
        const selectionValue = $(e.target).val();

        return getChoice(store, selectionType, selectionValue);
    });

    // On Submit
    $(document).on(this.scopedEvent('submit'), this.scopedSelector('.js-generic-email-form'), (e) => {
        // Clear room preferences
        $('.js-room-preference-option').prop('checked', false);
        selectedRoomPreferences.length = 0; // Set const array to []
        store.dispatch(changeRoomPreference(getPreferenceList(selectedRoomPreferences)));

        // Clear style preferences
        $('.js-style-preference-option').prop('checked', false);
        selectedStylePreferences.length = 0; // Set const array to []
        store.dispatch(changeStylePreference(getPreferenceList(selectedStylePreferences)));
    });
}

/**
 * Bootstrap the component onto the page
 * @param {object} store The redux store
 * @param {object} overrides Component-level overrides config
 * @returns {undefined}
 */
export function init(store, overrides = {}) {
    const config = {
        name: 'stylistPreferredLocationComponent',
        attachEvents: attachEvents,
        renderType: mDom,
        renderOptions: mDomMaintainInputVals,
        afterConstruction: afterConstruction,
        templateLocation: '.dom-generic-email-service',
        isComponentValid: templateLocationExists
    };

    return generate(config, store, overrides);
}

/**
 * Format preference array (styles/rooms) as readable ', ' delimited string
 * @param {array} preferenceList String array of selected styles or rooms
 * @returns {string} Formatted list
 */
function getPreferenceList(preferenceList) {
    return preferenceList.join(', ');
}
